/**
 * DeclarationsTab — Exportar Declaraciones Juradas y Libro de Remuneraciones.
 */
import { useMemo, useState } from 'react'
import { FileSpreadsheet, FileText } from 'lucide-react'
import DownloadButton from '@/components/DownloadButton'
import { usePeriods } from '../hooks/usePayroll'

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

/** Los estados cuyas liquidaciones entran al LRE (el generador filtra
 *  `approved`/`paid`): un período en borrador daría un archivo sin filas. */
const ESTADOS_CON_LRE = ['approved', 'paid', 'closed']

const CURRENT_YEAR = new Date().getFullYear()

export default function DeclarationsTab() {
  const [year, setYear] = useState(CURRENT_YEAR - 1)

  const years = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i)

  // El LRE es mensual y tiene su propio selector: el de arriba es el año
  // tributario de las DJ. Solo se ofrecen meses con liquidaciones aprobadas.
  const { data: periodosData, isLoading: cargandoPeriodos } = usePeriods()
  const periodosLre = useMemo(
    () => (periodosData?.periods ?? [])
      .filter(p => ESTADOS_CON_LRE.includes(p.status))
      .sort((a, b) => (b.year - a.year) || (b.month - a.month)),
    [periodosData],
  )
  const [periodoLreId, setPeriodoLreId] = useState('')
  const periodoLre = periodosLre.find(p => p.id === periodoLreId) ?? periodosLre[0]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-4">
        <label htmlFor="declarationstab-ano-tributario" className="text-sm font-medium text-content">Año tributario:</label>
        <select id="declarationstab-ano-tributario" value={year} onChange={e => setYear(parseInt(e.target.value))}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* DJ 1887 */}
        <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 hover:shadow-md transition-shadow shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <FileSpreadsheet size={20} className="text-blue-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-content">DJ 1887</h3>
              <p className="text-xs text-content-subtle">Rentas Art. 42 N°1</p>
            </div>
          </div>
          <p className="text-xs text-content-muted mb-4">
            Sueldos pagados y retenciones de Impuesto Único de Segunda Categoría del año {year}.
          </p>
          <DownloadButton path={`/payroll/dj/1887/?year=${year}`} filename={`DJ1887_${year}.csv`} />
        </div>

        {/* DJ 1879 */}
        <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 hover:shadow-md transition-shadow shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-warning-surface flex items-center justify-center">
              <FileSpreadsheet size={20} className="text-warning-solid" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-content">DJ 1879</h3>
              <p className="text-xs text-content-subtle">Honorarios Art. 42 N°2 / Directores</p>
            </div>
          </div>
          <p className="text-xs text-content-muted mb-4">
            Retenciones efectuadas a Boletas de Honorarios Electrónicas (BHE) y remuneraciones de directores del año {year}.
          </p>
          <DownloadButton path={`/payroll/dj/1879/?year=${year}`} filename={`DJ1879_${year}.csv`} />
        </div>

        {/* LRE — el archivo de carga masiva de la DT. ⚠️ No es el Excel del Libro
            de Remuneraciones que se baja desde Períodos (ese es para revisión):
            esta tarjeta mandaba justamente ahí. */}
        <div className="bg-glass backdrop-blur-glass border border-divider rounded-card p-6 hover:shadow-md transition-shadow shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-success-surface flex items-center justify-center">
              <FileText size={20} className="text-success-solid" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-content">LRE — Dirección del Trabajo</h3>
              <p className="text-xs text-content-subtle">Libro de Remuneraciones Electrónico</p>
            </div>
          </div>
          <p className="text-xs text-content-muted mb-4">
            Archivo de carga masiva para el portal Mi DT, con las liquidaciones aprobadas o
            pagadas del mes. Para revisar los montos se usa el Excel del Libro de
            Remuneraciones, en Períodos.
          </p>
          {cargandoPeriodos ? (
            <p className="text-xs text-content-subtle">Cargando períodos…</p>
          ) : periodoLre ? (
            <div className="space-y-3">
              <div>
                <label htmlFor="declarationstab-periodo-lre" className="block text-xs font-medium text-content mb-1">
                  Período
                </label>
                <select id="declarationstab-periodo-lre" value={periodoLre.id}
                  onChange={e => setPeriodoLreId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
                  {periodosLre.map(p => (
                    <option key={p.id} value={p.id}>
                      {MESES[p.month - 1]} {p.year} · {p.status_display}
                    </option>
                  ))}
                </select>
              </div>
              <DownloadButton
                path={`/payroll/lre-official/?year=${periodoLre.year}&month=${periodoLre.month}`}
                filename={`lre_${periodoLre.year}${String(periodoLre.month).padStart(2, '0')}.csv`}
                usarNombreDelServidor
              />
            </div>
          ) : (
            <p className="text-xs text-content-subtle">
              Todavía no hay períodos aprobados. El LRE se genera con las liquidaciones
              aprobadas o pagadas.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
